import { logError } from '@/logger/logger'
import { FeatureFlags } from '@/plugins/featureFlags'

interface CobrowseInitOptions {
  userName: string
  email?: string
  brand: string
  brandId: string
  brandLogoUrl?: string
  app: string
  [key: string]: unknown
}

interface CobrowseInstance {
  init: (options?: CobrowseInitOptions) => void
  stop: () => Promise<void>
  readonly sessionCode: string | null
}

declare global {
  interface Window {
    Cobrowse?: CobrowseInstance
  }
}

const FLAG_NAME = 'hermes.cobrowse'
const FLAG_WAIT_TIMEOUT_MS = 10_000

// Cobrowse is disabled outside production by default. Set VITE_COBROWSE_ENABLED=true
// and optionally VITE_COBROWSE_URL to test locally against a dev cobrowse server.
const IS_PRODUCTION = import.meta.env.MODE === 'production'
const FORCE_ENABLED = import.meta.env.VITE_COBROWSE_ENABLED === 'true'

const WIDGET_URL =
  import.meta.env.VITE_COBROWSE_URL ?? 'https://cobrowse.owner.sh/widget.js'

// Cached promise prevents concurrent loadScript() calls from injecting
// duplicate <script> tags — load() and init() can race on app boot.
let loadPromise: Promise<void> | null = null

/**
 * Check whether cobrowse should be active. Requires production mode (or
 * VITE_COBROWSE_ENABLED=true) AND the Unleash feature flag to be on.
 */
function isEnabled(): boolean {
  if (!IS_PRODUCTION && !FORCE_ENABLED) return false
  return FeatureFlags.isEnabled(FLAG_NAME)
}

/**
 * Cobrowse boots early (App.vue onMounted) but feature flags may not have
 * fetched yet. We wait for the Unleash client to emit 'ready' or 'update'
 * so the flag value is known before deciding whether to load the widget.
 * A 10 s timeout prevents hanging forever if Unleash is unreachable —
 * after timeout the caller falls through to isEnabled() which will return
 * false and skip loading.
 */
async function waitForFeatureFlags(): Promise<void> {
  if (!IS_PRODUCTION && !FORCE_ENABLED) return
  if (isEnabled()) return

  return await new Promise((resolve) => {
    const cleanup = () => {
      clearTimeout(timer)
      FeatureFlags.off('ready', onReady)
      FeatureFlags.off('update', onReady)
    }

    const onReady = () => {
      cleanup()
      resolve()
    }

    const timer = setTimeout(() => {
      cleanup()
      resolve()
    }, FLAG_WAIT_TIMEOUT_MS)

    FeatureFlags.on('ready', onReady)
    FeatureFlags.on('update', onReady)
  })
}

/**
 * Inject the cobrowse widget script tag and wait for it to load. The widget
 * exposes window.Cobrowse once ready.
 *
 * The load promise is cached so that concurrent callers (load() from App
 * mount + init() from auth) await the same operation instead of injecting
 * the script twice. On failure the cached promise resets so a retry is
 * possible on the next call.
 */
async function loadScript(): Promise<void> {
  if (window.Cobrowse) return
  if (loadPromise) return await loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = WIDGET_URL
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      loadPromise = null
      reject(new Error(`Failed to load cobrowse widget from ${WIDGET_URL}`))
    }
    document.head.appendChild(script)
  })

  return await loadPromise
}

/**
 * Get the three-word session code that identifies this customer on the
 * cobrowse dashboard. Loads the widget (which registers via WebSocket and
 * receives the code from the server), then polls until the code arrives.
 * The WebSocket handshake typically completes in <2s but we allow up to 10s.
 */
async function getSessionCode(): Promise<string | null> {
  try {
    if (!IS_PRODUCTION && !FORCE_ENABLED) return null
    await loadScript()

    // The widget needs time to connect via WebSocket and receive the
    // `registered` response containing the session code. Poll until ready.
    const MAX_WAIT = 10_000
    const INTERVAL = 300
    let waited = 0
    while (waited < MAX_WAIT) {
      const code = window.Cobrowse?.sessionCode ?? null
      if (code) return code
      await new Promise((resolve) => setTimeout(resolve, INTERVAL))
      waited += INTERVAL
    }
    return null
  } catch (e) {
    logError(e, { context: 'cobrowse.getSessionCode' })
    return null
  }
}

export const cobrowse = {
  getSessionCode,

  /**
   * Initialize cobrowse with user/brand metadata. Called after authentication
   * (both fresh login via useAuthActions and session restore via the
   * checkAuthenticated route guard) so the widget knows who is browsing.
   * Waits for feature flags first since the widget shouldn't load at all
   * when disabled.
   * @param options
   */
  async init(options: CobrowseInitOptions): Promise<void> {
    try {
      await waitForFeatureFlags()
      if (!isEnabled()) return

      await loadScript()

      if (!window.Cobrowse) {
        logError(new Error('window.Cobrowse not available after script load'), {
          context: 'cobrowse.init',
        })
        return
      }

      window.Cobrowse.init(options)
    } catch (e) {
      logError(e, { context: 'cobrowse.init' })
    }
  },

  /**
   * Pre-load the widget script so it's ready by the time init() is called.
   * Fired from App.vue onMounted — the script loads in the background while
   * the rest of the app boots.
   */
  async load(): Promise<void> {
    try {
      await waitForFeatureFlags()
      if (!isEnabled()) return
      await loadScript()
    } catch (e) {
      logError(e, { context: 'cobrowse.load' })
    }
  },

  /**
   * Stop an active cobrowse session. No-op if the widget hasn't loaded.
   */
  async stop(): Promise<void> {
    try {
      if (!window.Cobrowse) return
      await window.Cobrowse.stop()
    } catch (e) {
      logError(e, { context: 'cobrowse.stop' })
    }
  },
}
